import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Badge, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import {
  faArrowLeft,
  faArrowRight,
  faBan,
  faCheck,
  faExclamationTriangle,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';
import { useDebounce } from 'use-debounce';
import { useHistory, useParams } from 'react-router-dom';

import {
  backgroundGrey,
  blackberry,
  coolGrey,
  grape,
  greyStroke,
  modernSalmon,
  modernSalmonBackground,
  positiveGreen,
  shadowDark,
  shadowVeryLight,
  softAmber,
  softBlue,
  softBlueLight,
  softGreenDark,
  softGreenLight,
  softPurple,
  softPurpleLight,
  white,
} from 'lib/css/colors';
import {
  BRAND_SKU_MAPPER_REVIEW_STATUS_TABS,
  BrandSkuMapperReviewAction,
  BrandSkuMapperReviewStatus,
  BrandSkuMapperRunRequestField,
  BrandSkuMapperRunStatus,
} from 'lib/enums/BrandSkuMapperEnums';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';
import {
  BRAND_SKU_MAPPER_ALL_BRANDS_START,
  BRAND_SKU_MAPPER_AUTO_APPROVE_GTIN,
  BRAND_SKU_MAPPER_PRODUCTS,
  BRAND_SKU_MAPPER_REVIEW_CANDIDATE,
  getURL,
} from 'lib/networking/endpoints';
import { httpPost } from 'lib/networking/http';
import { selectStyles } from 'lib/css/selectStyles';
import SwalDialog, { confirmRichAction, SwalToast } from 'lib/utils/toast';
import BrandSkuMapperProductCard from 'components/brand_sku_mapper/BrandSkuMapperProductCard';
import Container from 'components/shared/Container';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import TabBar from 'components/shared/TabBar';

const PAGE_SIZE = 20;

const STATUS_TABS = BRAND_SKU_MAPPER_REVIEW_STATUS_TABS;

const DEFAULT_MIN_CONFIDENCE = 75;

const CONFIDENCE_OPTIONS = [
  { value: DEFAULT_MIN_CONFIDENCE, label: '≥ 75%' },
  { value: 50, label: '≥ 50%' },
  { value: 90, label: '≥ 90%' },
  { value: null, label: 'All scores' },
];

function BrandSkuMapperRunReviewView() {
  // mfrId is present on the /manufacturers/:mfrId/products route,
  // absent on the /all-gmv-products route.
  const { mfrId } = useParams();
  const isAllGmvView = !mfrId;
  const history = useHistory();

  const [page, setPage] = useState(0);
  const [status, setStatus] = useState(BrandSkuMapperReviewStatus.PENDING);
  const [searchInput, setSearchInput] = useState('');
  const [query] = useDebounce(searchInput, 400);
  const [brandId, setBrandId] = useState('');
  const [distributorId, setDistributorId] = useState('');
  const [dpPartnerOnly, setDpPartnerOnly] = useState(true);
  const [minConfidence, setMinConfidence] = useState(DEFAULT_MIN_CONFIDENCE);
  const [pageIdx, setPageIdx] = useState(0);
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [selectedAltId, setSelectedAltId] = useState(null);
  const [reviewingAction, setReviewingAction] = useState(null);
  const pendingLastRef = useRef(false);
  const reviewedIdRef = useRef(null);

  useEffect(() => {
    setPage(0);
  }, [query]);

  const params = {
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };
  if (mfrId) {
    params.manufacturer_id = mfrId;
  }
  if (status) {
    params.status = status;
  }
  if (query) {
    params.query = query;
  }
  if (brandId) {
    params.brand_id = brandId;
  }
  if (distributorId) {
    params.distributor_id = distributorId;
  }
  if (dpPartnerOnly) {
    params.dp_partner_only = true;
  }
  if (minConfidence != null) {
    params.min_confidence = minConfidence;
  }

  const { data, loading, refetch, silentRefetch } = useGet(BRAND_SKU_MAPPER_PRODUCTS, params);

  const latestRun = data?.latest_run || null;
  const candidates = useMemo(() => data?.candidates || [], [data]);
  const total = data?.count || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const countsByStatus = data?.counts_by_status || {};
  const brands = useMemo(() => data?.brands || [], [data]);
  const distributors = useMemo(() => data?.distributors || [], [data]);

  const runIsRunning = latestRun?.status === BrandSkuMapperRunStatus.RUNNING;
  const runIsPending = latestRun?.status === BrandSkuMapperRunStatus.PENDING;
  const runIsFailed = latestRun?.status === BrandSkuMapperRunStatus.FAILED;
  const runIsCompleted = latestRun?.status === BrandSkuMapperRunStatus.COMPLETED;

  // Poll every 4 s while a run is active so the status pill updates without
  // the user needing to manually refresh. Uses silentRefetch to avoid toggling
  // the loading state (which would flash the spinner over the candidate view).
  useEffect(() => {
    if (!isAllGmvView || (!runIsRunning && !runIsPending)) return undefined;
    const id = setInterval(silentRefetch, 4000);
    return () => clearInterval(id);
  }, [isAllGmvView, runIsRunning, runIsPending, silentRefetch]);

  useEffect(() => {
    if (pendingLastRef.current && candidates.length > 0) {
      setPageIdx(candidates.length - 1);
      pendingLastRef.current = false;
    }
  }, [candidates]);

  useEffect(() => {
    setPageIdx(0);
    setShowAlternatives(false);
  }, [status, query, brandId, distributorId, dpPartnerOnly, minConfidence]);

  useEffect(() => {
    setShowAlternatives(false);
  }, [page, pageIdx]);

  const safeIdx = Math.min(pageIdx, Math.max(candidates.length - 1, 0));
  const candidate = candidates[safeIdx];
  const globalIdx = page * PAGE_SIZE + safeIdx;

  useEffect(() => {
    setSelectedAltId(null);
  }, [candidate?.id]);

  const displayedMfrProduct = useMemo(() => {
    if (!candidate) return null;
    if (selectedAltId && candidate.alternative_candidates) {
      const alt = candidate.alternative_candidates.find(a => a.mfr_product_id === selectedAltId);
      if (alt) return alt;
    }
    return candidate.mfr_product;
  }, [candidate, selectedAltId]);

  const atFirst = page === 0 && safeIdx === 0;
  const atLast = page >= totalPages - 1 && safeIdx >= candidates.length - 1;

  const goPrev = useCallback(() => {
    if (safeIdx > 0) {
      setPageIdx(safeIdx - 1);
    } else if (page > 0) {
      pendingLastRef.current = true;
      setPage(page - 1);
    }
  }, [page, safeIdx]);

  const goNext = useCallback(() => {
    if (safeIdx < candidates.length - 1) {
      setPageIdx(safeIdx + 1);
    } else if (page < totalPages - 1) {
      pendingLastRef.current = false;
      setPage(page + 1);
      setPageIdx(0);
    }
  }, [candidates.length, page, safeIdx, totalPages]);

  const review = useCallback(
    (c, action, pickedMfrProductId = null) => {
      if (!c || reviewingAction !== null) return;
      setReviewingAction(action);
      httpPost(getURL(BRAND_SKU_MAPPER_REVIEW_CANDIDATE, { candidate_id: c.id }), {
        action,
        picked_mfr_product_id: pickedMfrProductId,
      })
        .then(() => {
          if (!atLast) {
            reviewedIdRef.current = c.id;
          }
          refetch();
        })
        .catch(err => {
          const msg = err?.response?.data?.message || 'Failed to update candidate.';
          SwalDialog('error', msg, 'Error');
        })
        .finally(() => {
          setReviewingAction(null);
        });
    },
    [atLast, refetch, reviewingAction],
  );

  useEffect(() => {
    if (!reviewedIdRef.current) return;
    const stillPresent = candidates.some(c => c.id === reviewedIdRef.current);
    reviewedIdRef.current = null;
    if (stillPresent) {
      goNext();
    }
  }, [candidates, goNext]);

  useEffect(() => {
    const isTyping = () => {
      const tag = document.activeElement?.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable;
    };
    const handler = e => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTyping() || reviewingAction !== null) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      } else if (!candidate) {
        return;
      } else if (e.key === 'a' || e.key === 'A') {
        if (
          displayedMfrProduct &&
          candidate.review_status !== BrandSkuMapperReviewStatus.APPROVED
        ) {
          e.preventDefault();
          review(candidate, BrandSkuMapperReviewAction.APPROVE, selectedAltId);
        }
      } else if (e.key === 'r' || e.key === 'R') {
        if (candidate.review_status !== BrandSkuMapperReviewStatus.REJECTED) {
          e.preventDefault();
          review(candidate, BrandSkuMapperReviewAction.REJECT);
        }
      } else if (e.key === 'n' || e.key === 'N') {
        if (candidate.review_status !== BrandSkuMapperReviewStatus.NO_MATCH) {
          e.preventDefault();
          review(candidate, BrandSkuMapperReviewAction.NO_MATCH);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [candidate, displayedMfrProduct, goNext, goPrev, review, reviewingAction, selectedAltId]);

  const brandOptions = useMemo(
    () => brands.map(b => ({ value: b.id, label: `${b.name} (${b.count})` })),
    [brands],
  );
  const selectedBrandOption = brandOptions.find(o => String(o.value) === String(brandId)) || null;

  const distributorOptions = useMemo(
    () => distributors.map(d => ({ value: d.id, label: `${d.name} (${d.count})` })),
    [distributors],
  );
  const selectedDistributorOption =
    distributorOptions.find(o => String(o.value) === String(distributorId)) || null;

  const confidenceColor = c => {
    if (c >= 80) return positiveGreen;
    if (c >= 50) return softAmber;
    return modernSalmon;
  };

  const { postData: dispatchAllBrandsRun, loading: dispatchingRun } = usePost(
    BRAND_SKU_MAPPER_ALL_BRANDS_START,
    () => {
      SwalToast('success', 'New analysis started.');
      refetch();
    },
    err => {
      const msg = err?.response?.data?.message || 'Could not start a new run.';
      SwalToast('error', msg);
    },
  );

  const { postData: autoApproveGtin, loading: autoApproving } = usePost(
    latestRun?.id
      ? getURL(BRAND_SKU_MAPPER_AUTO_APPROVE_GTIN, { run_id: latestRun.id })
      : BRAND_SKU_MAPPER_AUTO_APPROVE_GTIN,
    () => {
      SwalToast('success', 'Auto-approve task dispatched. Candidates will update shortly.');
      refetch();
    },
    err => {
      const msg = err?.response?.data?.message || 'Could not auto-approve GTIN matches.';
      SwalToast('error', msg);
    },
  );

  const handleAutoApproveGtin = useCallback(() => {
    confirmRichAction(
      () => autoApproveGtin({}),
      'All PENDING candidates with a <strong>100% GTIN match</strong> will be approved ' +
        'automatically. Candidates already linked to a <em>different</em> mfr product ' +
        'will be rejected instead.',
      'Auto-resolve GTIN & linked candidates?',
      'Yes, approve',
    );
  }, [autoApproveGtin]);

  const handleNewAllBrandsRun = useCallback(
    ({ remainingOnly }) => {
      const confirmTitle = remainingOnly ? 'Run for new products only?' : 'Run for all products?';
      const confirmHtml = remainingOnly
        ? 'Only GMV products that have never been analyzed will be processed.'
        : 'Re-analyze <strong>every</strong> GMV product. ' +
          'Already-analyzed products will consume Claude tokens.';
      confirmRichAction(
        () =>
          dispatchAllBrandsRun({ [BrandSkuMapperRunRequestField.REMAINING_ONLY]: remainingOnly }),
        confirmHtml,
        confirmTitle,
        remainingOnly ? 'Yes, run for new only' : 'Yes, run for all',
      );
    },
    [dispatchAllBrandsRun],
  );

  return (
    <Container className={css(styles.container)}>
      <Button
        variant="link"
        className={css(styles.backLink)}
        onClick={() => {
          if (isAllGmvView) {
            history.push('/enrich-products/brand-sku-mapper');
          } else {
            history.push(`/enrich-products/brand-sku-mapper/manufacturers/${mfrId}`);
          }
        }}
      >
        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
        {isAllGmvView ? 'Back to brand SKU mapper' : 'Back to manufacturer'}
      </Button>

      <div className={css(styles.headerRow)}>
        <div className={css(styles.headerLeft)}>
          <span className={css(styles.title)}>
            {isAllGmvView ? 'All GMV Products' : 'Products'}
          </span>
          {latestRun && !runIsRunning && !runIsPending && (
            <span className={css(styles.statusPill)}>{latestRun.status}</span>
          )}
          {latestRun && (
            <span className={css(styles.headerMeta)}>
              {latestRun.matched_count || 0}/{latestRun.total_vendor_products || 0} matched
              {!isAllGmvView && ` · Pool ${latestRun.mfr_product_pool_size || 0}`}
              {' · '}Vision {latestRun.use_vision ? 'ON' : 'OFF'}
            </span>
          )}
          {isAllGmvView && (runIsFailed || runIsCompleted || !latestRun) && (
            <>
              <Button
                size="sm"
                className={css(styles.dispatchBtn)}
                onClick={() => handleNewAllBrandsRun({ remainingOnly: false })}
                disabled={dispatchingRun}
                title="Re-analyze every GMV product (uses Claude tokens for already-analyzed ones)"
              >
                Run for All
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                className={css(styles.newRunBtn)}
                onClick={() => handleNewAllBrandsRun({ remainingOnly: true })}
                disabled={dispatchingRun}
                title="Only analyze GMV products that have never been analyzed before"
              >
                Run for New Only
              </Button>
              {(runIsCompleted || runIsFailed) && latestRun?.id && (
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className={css(styles.newRunBtn)}
                  onClick={handleAutoApproveGtin}
                  disabled={autoApproving}
                  title="Auto-approve 100% GTIN matches and resolve externally-linked candidates"
                >
                  {autoApproving ? (
                    <>
                      <Spinner animation="border" size="sm" className="mr-1" />
                      Approving…
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheck} className="mr-1" />
                      Resolve GTIN & Linked
                    </>
                  )}
                </Button>
              )}
            </>
          )}
          {isAllGmvView && (runIsRunning || runIsPending) && (
            <span className={css(styles.runningIndicator)}>
              <Spinner animation="border" size="sm" className="mr-1" />
              {runIsPending ? 'Queued…' : 'Running…'}
            </span>
          )}
        </div>
        <Badge className={css(styles.totalBadge)}>{total.toLocaleString()} candidates</Badge>
      </div>

      <TabBar
        tabs={STATUS_TABS.map(t => ({
          key: t.key,
          label: t.label,
          count: t.key && countsByStatus[t.key] != null ? countsByStatus[t.key] : undefined,
        }))}
        activeKey={status}
        onSelect={key => {
          setStatus(key);
          setPage(0);
        }}
      />

      <Row className="mt-3 align-items-center flex-wrap g-2">
        <Col md={isAllGmvView ? 2 : 3}>
          <Form.Control
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Search vendor product name or SKU"
          />
        </Col>
        <Col md={isAllGmvView ? 2 : 3}>
          <Select
            isClearable
            options={brandOptions}
            value={selectedBrandOption}
            onChange={opt => {
              setBrandId(opt ? opt.value : '');
              setPage(0);
            }}
            placeholder="Filter by brand"
            styles={selectStyles}
            aria-label="Filter by brand"
          />
        </Col>
        {isAllGmvView && (
          <Col md={2}>
            <Select
              isClearable
              options={distributorOptions}
              value={selectedDistributorOption}
              onChange={opt => {
                setDistributorId(opt ? opt.value : '');
                setPage(0);
              }}
              placeholder="Filter by distributor"
              styles={selectStyles}
              aria-label="Filter by distributor"
            />
          </Col>
        )}
        <Col md={isAllGmvView ? 2 : 3}>
          <Select
            options={CONFIDENCE_OPTIONS}
            value={CONFIDENCE_OPTIONS.find(o => o.value === minConfidence) || null}
            onChange={opt => {
              setMinConfidence(opt ? opt.value : null);
              setPage(0);
            }}
            placeholder="Min score"
            styles={selectStyles}
            aria-label="Filter by minimum confidence score"
          />
        </Col>
        <Col md="auto" className="d-flex align-items-center">
          <div
            role="switch"
            aria-checked={dpPartnerOnly}
            tabIndex={0}
            onClick={() => {
              setDpPartnerOnly(p => !p);
              setPage(0);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setDpPartnerOnly(p => !p);
                setPage(0);
              }
            }}
            className={css(styles.toggleWrapper)}
            aria-label="Show only DP partner distributor products"
          >
            <div className={css(styles.toggleTrack, dpPartnerOnly && styles.toggleTrackOn)}>
              <div className={css(styles.toggleThumb, dpPartnerOnly && styles.toggleThumbOn)} />
            </div>
            <span className={css(styles.toggleText)}>DP Partners Only</span>
          </div>
        </Col>
        {(searchInput ||
          brandId ||
          distributorId ||
          !dpPartnerOnly ||
          minConfidence !== DEFAULT_MIN_CONFIDENCE) && (
          <Col md="auto">
            <Button
              variant="link"
              onClick={() => {
                setSearchInput('');
                setBrandId('');
                setDistributorId('');
                setDpPartnerOnly(true);
                setMinConfidence(DEFAULT_MIN_CONFIDENCE);
                setPage(0);
              }}
            >
              Clear
            </Button>
          </Col>
        )}
      </Row>
      <Row className="mt-2 align-items-center">
        <Col className="ml-auto d-flex justify-content-end">
          <div className={css(styles.legend)}>
            <span className={css(styles.legendTitle)}>Shortcuts</span>
            <span className={css(styles.legendItem)}>
              <kbd className={css(styles.kbd)}>←</kbd> <kbd className={css(styles.kbd)}>→</kbd>{' '}
              navigate
            </span>
            <span className={css(styles.legendItem)}>
              <kbd className={css(styles.kbd)}>A</kbd> approve
            </span>
            <span className={css(styles.legendItem)}>
              <kbd className={css(styles.kbd)}>R</kbd> reject
            </span>
            <span className={css(styles.legendItem)}>
              <kbd className={css(styles.kbd)}>N</kbd> no match
            </span>
          </div>
        </Col>
      </Row>

      {loading ? (
        <LoadingSpinner />
      ) : !candidate ? (
        <div className="text-center text-muted p-5">No candidates to review.</div>
      ) : (
        <div className={css(styles.reviewWrapper)}>
          <div className={css(styles.navRow)}>
            <Button
              onClick={goPrev}
              disabled={atFirst || reviewingAction !== null}
              className={css(styles.navBtn)}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              Prev
            </Button>
            <div className={css(styles.positionText)}>
              {total > 0 ? `${globalIdx + 1} of ${total.toLocaleString()}` : '—'}
            </div>
            <Button
              onClick={goNext}
              disabled={atLast || reviewingAction !== null}
              className={css(styles.navBtn)}
            >
              Next
              <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
            </Button>
          </div>

          {/* Review status banner */}
          {(() => {
            const rs = candidate.review_status || BrandSkuMapperReviewStatus.PENDING;
            const statusMeta = {
              [BrandSkuMapperReviewStatus.APPROVED]: {
                icon: faCheck,
                bg: softGreenLight,
                color: positiveGreen,
                label: 'Approved',
              },
              [BrandSkuMapperReviewStatus.REJECTED]: {
                icon: faTimes,
                bg: modernSalmonBackground,
                color: modernSalmon,
                label: 'Rejected',
              },
              [BrandSkuMapperReviewStatus.NO_MATCH]: {
                icon: faBan,
                bg: greyStroke,
                color: coolGrey,
                label: 'No match exists',
              },
              [BrandSkuMapperReviewStatus.PENDING]: {
                icon: null,
                bg: softPurpleLight,
                color: softPurple,
                label: 'Pending review',
              },
            }[rs];

            const reviewer = candidate.reviewed_by;
            const reviewedAt = candidate.reviewed_at;
            let reviewedAtLabel = null;
            if (reviewedAt) {
              const d = new Date(reviewedAt);
              if (!Number.isNaN(d.getTime())) {
                reviewedAtLabel = d.toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                });
              }
            }
            const showReviewerMeta =
              rs !== BrandSkuMapperReviewStatus.PENDING && (reviewer || reviewedAtLabel);

            return (
              <div
                className={css(styles.statusBanner)}
                style={{ backgroundColor: statusMeta.bg, color: statusMeta.color }}
              >
                {statusMeta.icon && (
                  <FontAwesomeIcon
                    icon={statusMeta.icon}
                    className={css(styles.statusBannerIcon)}
                  />
                )}
                <div className={css(styles.statusBannerText)}>
                  <span className={css(styles.statusBannerLabel)}>{statusMeta.label}</span>
                  {showReviewerMeta && (
                    <span className={css(styles.statusBannerMeta)}>
                      {reviewer ? `by ${reviewer}` : ''}
                      {reviewer && reviewedAtLabel ? ' • ' : ''}
                      {reviewedAtLabel || ''}
                    </span>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Already-linked externally disclaimer */}
          {candidate.is_already_linked && (
            <div className={css(styles.alreadyLinkedBanner)}>
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
              This product is already linked to a manufacturer SKU externally. You can safely ignore
              this candidate.
            </div>
          )}

          {candidate.reasoning && (
            <div className={css(styles.agentBanner)}>
              <div className={css(styles.confidence)}>
                <span
                  className={css(styles.confidenceValue)}
                  style={{ color: confidenceColor(candidate.confidence || 0) }}
                >
                  {candidate.confidence || 0}%
                </span>
                <div className={css(styles.confidenceLabel)}>Agent confidence</div>
              </div>
              <div className={css(styles.reasoning)}>{candidate.reasoning}</div>
            </div>
          )}

          <Row className="mt-3">
            <Col md={6}>
              <BrandSkuMapperProductCard
                product={candidate.vendor_product}
                kind="vendor"
                gmv={candidate.gmv}
              />
            </Col>
            <Col md={6}>
              {displayedMfrProduct ? (
                <BrandSkuMapperProductCard product={displayedMfrProduct} kind="mfr" />
              ) : (
                <Card className={css(styles.productCard)}>
                  <Card.Body>
                    <div className={css(styles.sideLabel)}>MFR suggested match</div>
                    <div className={css(styles.mutedText) + ' mt-4'}>No match suggested.</div>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>

          <Row className={css(styles.actionsRow)}>
            <Col xs={3}>
              <Button
                block
                className={css(styles.approveBtn)}
                onClick={() => review(candidate, BrandSkuMapperReviewAction.APPROVE, selectedAltId)}
                disabled={
                  reviewingAction !== null ||
                  candidate.review_status === BrandSkuMapperReviewStatus.APPROVED ||
                  !displayedMfrProduct
                }
              >
                {reviewingAction === BrandSkuMapperReviewAction.APPROVE ? (
                  <Spinner animation="border" size="sm" className="mr-2" />
                ) : (
                  <FontAwesomeIcon icon={faCheck} className="mr-2" />
                )}
                Approve match
              </Button>
            </Col>
            <Col xs={3}>
              <Button
                block
                className={css(styles.rejectBtn)}
                onClick={() => review(candidate, BrandSkuMapperReviewAction.REJECT)}
                disabled={
                  reviewingAction !== null ||
                  candidate.review_status === BrandSkuMapperReviewStatus.REJECTED
                }
              >
                {reviewingAction === BrandSkuMapperReviewAction.REJECT ? (
                  <Spinner animation="border" size="sm" className="mr-2" />
                ) : (
                  <FontAwesomeIcon icon={faTimes} className="mr-2" />
                )}
                Reject
              </Button>
            </Col>
            <Col xs={3}>
              <Button
                block
                className={css(styles.neutralBtn)}
                onClick={() => review(candidate, BrandSkuMapperReviewAction.NO_MATCH)}
                disabled={
                  reviewingAction !== null ||
                  candidate.review_status === BrandSkuMapperReviewStatus.NO_MATCH
                }
              >
                {reviewingAction === BrandSkuMapperReviewAction.NO_MATCH ? (
                  <Spinner animation="border" size="sm" className="mr-2" />
                ) : (
                  <FontAwesomeIcon icon={faBan} className="mr-2" />
                )}
                No match exists
              </Button>
            </Col>
            <Col xs={3}>
              <Button
                block
                className={css(styles.pickDifferentBtn)}
                onClick={() => setShowAlternatives(v => !v)}
                disabled={
                  reviewingAction !== null ||
                  !candidate.alternative_candidates ||
                  candidate.alternative_candidates.length === 0
                }
              >
                {showAlternatives ? 'Hide alternatives' : 'Pick different'}
                {candidate.alternative_candidates && candidate.alternative_candidates.length > 0
                  ? ` (${candidate.alternative_candidates.length})`
                  : ''}
              </Button>
            </Col>
          </Row>

          {showAlternatives &&
            candidate.alternative_candidates &&
            candidate.alternative_candidates.length > 0 && (
              <>
                <div className={css(styles.altLabel)}>Alternative candidates</div>
                <Row>
                  {candidate.alternative_candidates.map(alt => {
                    const isSelected = selectedAltId === alt.mfr_product_id;
                    return (
                      <Col md={4} key={alt.mfr_product_id} className="mb-2">
                        <div className={css(styles.altCard, isSelected && styles.altCardSelected)}>
                          <div className="d-flex align-items-center">
                            {alt.thumbnail_url ? (
                              <img
                                src={alt.thumbnail_url}
                                alt=""
                                className={css(styles.altImage)}
                                onError={e => {
                                  e.target.onerror = null;
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className={css(styles.altImagePlaceholder)} />
                            )}
                            <div className="flex-grow-1">
                              <div className={css(styles.altName)}>{alt.name}</div>
                              <div className={css(styles.mutedText)}>
                                {alt.brand || '—'} · {alt.pack_size || '—'}
                              </div>
                              <div
                                className={css(styles.mutedText)}
                                style={{ color: confidenceColor(alt.confidence || 0) }}
                              >
                                {alt.confidence || 0}%
                              </div>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            className={
                              css(styles.pickBtn, isSelected && styles.pickBtnSelected) +
                              ' mt-2 w-100'
                            }
                            onClick={() => setSelectedAltId(isSelected ? null : alt.mfr_product_id)}
                            disabled={
                              candidate.review_status === BrandSkuMapperReviewStatus.APPROVED
                            }
                          >
                            {isSelected ? '✓ Selected — click to revert' : 'Select this'}
                          </Button>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </>
            )}
        </div>
      )}
    </Container>
  );
}

const styles = StyleSheet.create({
  container: {
    minHeight: '100vh',
    backgroundColor: backgroundGrey,
  },
  backLink: {
    color: coolGrey,
    padding: 0,
    marginBottom: 16,
    ':hover': {
      color: blackberry,
      textDecoration: 'none',
    },
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
    borderLeft: `4px solid ${grape}`,
    paddingLeft: 12,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flex: 1,
  },
  headerMeta: {
    color: coolGrey,
    fontSize: 12,
  },
  title: {
    color: blackberry,
    fontWeight: 700,
    fontSize: 18,
    margin: 0,
  },
  statusPill: {
    marginLeft: 4,
    padding: '4px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    backgroundColor: softPurpleLight,
    color: softPurple,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  statusBanner: {
    marginTop: 16,
    padding: '14px 20px',
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    fontWeight: 700,
    fontSize: 16,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  statusBannerIcon: {
    fontSize: 22,
  },
  statusBannerText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
  },
  statusBannerLabel: {
    lineHeight: 1,
  },
  statusBannerMeta: {
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: 0.4,
    opacity: 0.85,
    textTransform: 'none',
  },
  alreadyLinkedBanner: {
    marginTop: 12,
    padding: '12px 16px',
    borderRadius: 10,
    backgroundColor: softBlueLight,
    color: softBlue,
    fontWeight: 500,
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
  },
  mutedText: {
    color: coolGrey,
    fontSize: 12,
  },
  totalBadge: {
    backgroundColor: softPurpleLight,
    color: blackberry,
    fontSize: 13,
    padding: '6px 10px',
  },
  legend: {
    display: 'inline-flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 10,
    fontSize: 11,
    color: coolGrey,
  },
  legendTitle: {
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    color: blackberry,
  },
  legendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  kbd: {
    display: 'inline-block',
    padding: '2px 6px',
    borderRadius: 4,
    border: `1px solid ${greyStroke}`,
    backgroundColor: backgroundGrey,
    color: blackberry,
    fontFamily: 'monospace',
    fontSize: 11,
    lineHeight: 1,
    minWidth: 18,
    textAlign: 'center',
  },
  dispatchBtn: {
    backgroundColor: grape,
    borderColor: grape,
    color: white,
    fontWeight: 600,
    fontSize: 12,
    padding: '4px 12px',
    ':hover': {
      backgroundColor: blackberry,
      borderColor: blackberry,
      color: white,
    },
  },
  newRunBtn: {
    fontSize: 12,
    padding: '4px 12px',
    fontWeight: 600,
  },
  runningIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 12,
    color: softAmber,
    fontWeight: 600,
    gap: 4,
  },
  toggleWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  },
  toggleTrack: {
    position: 'relative',
    width: 36,
    height: 20,
    borderRadius: 10,
    backgroundColor: greyStroke,
    flexShrink: 0,
    transition: 'background-color 0.2s',
  },
  toggleTrackOn: {
    backgroundColor: grape,
  },
  toggleThumb: {
    position: 'absolute',
    top: 2,
    left: 2,
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: white,
    boxShadow: `0 1px 3px ${shadowDark}`,
    transition: 'left 0.2s',
  },
  toggleThumbOn: {
    left: 18,
  },
  toggleText: {
    fontSize: 13,
    fontWeight: 500,
    color: blackberry,
  },
  reviewWrapper: {
    maxWidth: 1100,
    margin: '0 auto',
  },
  navRow: {
    marginTop: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  navBtn: {
    minWidth: 100,
    backgroundColor: blackberry,
    borderColor: blackberry,
    color: white,
    fontWeight: 600,
    ':hover': {
      backgroundColor: grape,
      borderColor: grape,
      color: white,
    },
    ':focus': {
      backgroundColor: grape,
      borderColor: grape,
      color: white,
      boxShadow: `0 0 0 0.2rem ${softPurpleLight}`,
    },
    ':disabled': {
      backgroundColor: greyStroke,
      borderColor: greyStroke,
      color: white,
    },
  },
  positionText: {
    color: coolGrey,
    fontSize: 13,
    fontWeight: 600,
  },
  agentBanner: {
    marginTop: 16,
    padding: 16,
    borderRadius: 12,
    backgroundColor: softGreenLight,
    display: 'flex',
    gap: 16,
    alignItems: 'flex-start',
  },
  confidence: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    minWidth: 90,
  },
  confidenceValue: {
    fontSize: 30,
    fontWeight: 700,
    lineHeight: 1,
  },
  confidenceLabel: {
    marginTop: 4,
    fontSize: 12,
    color: coolGrey,
  },
  reasoning: {
    flex: 1,
    fontSize: 13,
    color: blackberry,
    lineHeight: 1.5,
  },
  productCard: {
    borderRadius: 12,
    boxShadow: `0 1px 3px ${shadowVeryLight}`,
    height: '100%',
  },
  sideLabel: {
    color: coolGrey,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    fontWeight: 700,
    marginBottom: 12,
  },
  actionsRow: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: backgroundGrey,
    marginTop: 12,
    paddingTop: 12,
    paddingBottom: 16,
    zIndex: 10,
    borderTop: `1px solid ${greyStroke}`,
  },
  altLabel: {
    color: coolGrey,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    fontWeight: 700,
    marginTop: 16,
    marginBottom: 8,
  },
  altCard: {
    border: `1px solid ${greyStroke}`,
    borderRadius: 8,
    padding: 10,
    backgroundColor: white,
  },
  altCardSelected: {
    borderColor: grape,
    boxShadow: `0 0 0 2px ${softPurpleLight}`,
    backgroundColor: softPurpleLight,
  },
  altImage: {
    width: 48,
    height: 48,
    objectFit: 'contain',
    borderRadius: 6,
    marginRight: 10,
    border: `1px solid ${greyStroke}`,
  },
  altImagePlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 6,
    marginRight: 10,
    backgroundColor: softPurpleLight,
  },
  altName: {
    color: blackberry,
    fontWeight: 600,
    fontSize: 13,
  },
  pickBtn: {
    fontSize: 12,
    backgroundColor: white,
    borderColor: grape,
    color: blackberry,
    fontWeight: 600,
    ':hover': {
      backgroundColor: grape,
      borderColor: grape,
      color: white,
    },
    ':focus': {
      boxShadow: `0 0 0 0.2rem ${softPurpleLight}`,
    },
    ':disabled': {
      backgroundColor: white,
      borderColor: greyStroke,
      color: coolGrey,
    },
  },
  pickBtnSelected: {
    backgroundColor: grape,
    borderColor: grape,
    color: white,
    ':hover': {
      backgroundColor: blackberry,
      borderColor: blackberry,
      color: white,
    },
  },
  approveBtn: {
    backgroundColor: positiveGreen,
    borderColor: positiveGreen,
    color: white,
    fontWeight: 600,
    ':hover': {
      backgroundColor: softGreenDark,
      borderColor: softGreenDark,
    },
  },
  rejectBtn: {
    backgroundColor: modernSalmonBackground,
    borderColor: modernSalmonBackground,
    color: modernSalmon,
    fontWeight: 600,
    ':hover': {
      backgroundColor: modernSalmon,
      borderColor: modernSalmon,
      color: white,
    },
  },
  neutralBtn: {
    fontWeight: 600,
    backgroundColor: white,
    borderColor: grape,
    color: blackberry,
    ':hover': {
      backgroundColor: softPurpleLight,
      borderColor: grape,
      color: blackberry,
    },
    ':focus': {
      boxShadow: `0 0 0 0.2rem ${softPurpleLight}`,
    },
    ':disabled': {
      backgroundColor: white,
      borderColor: greyStroke,
      color: coolGrey,
    },
  },
  pickDifferentBtn: {
    fontWeight: 600,
    backgroundColor: white,
    borderColor: blackberry,
    color: blackberry,
    ':hover': {
      backgroundColor: blackberry,
      borderColor: blackberry,
      color: white,
    },
    ':focus': {
      boxShadow: `0 0 0 0.2rem ${softPurpleLight}`,
    },
    ':disabled': {
      backgroundColor: white,
      borderColor: greyStroke,
      color: coolGrey,
    },
  },
});

export default BrandSkuMapperRunReviewView;
